import { FormEvent, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LuArrowRight, LuCheck, LuX } from "react-icons/lu";
import { useLang } from "../i18n";
import { demoFormContent } from "../data/content";
import "./DemoModal.css";

type DemoModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type Status = "idle" | "submitting" | "success" | "error";

const FORM_NAME = "demo";

/**
 * Encode form data the way Netlify expects for AJAX form submissions:
 * application/x-www-form-urlencoded with `form-name` as the form identifier.
 */
function encode(data: Record<string, string>) {
  return Object.entries(data)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");
}

export default function DemoModal({ isOpen, onClose }: DemoModalProps) {
  const { t } = useLang();
  const fields = demoFormContent.fields;
  const [status, setStatus] = useState<Status>("idle");
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    // Fallback for browsers without `scrollbar-gutter: stable`: measure the
    // current scrollbar width and pad the body + any position:fixed elements
    // (e.g. the site header) so the page doesn't shift when overflow is hidden.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPadding = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    const fixedEls = Array.from(
      document.querySelectorAll<HTMLElement>(".site-header"),
    );
    const prevFixedPadding = fixedEls.map((el) => el.style.paddingRight);
    if (scrollbarWidth > 0) {
      fixedEls.forEach((el) => {
        el.style.paddingRight = `${scrollbarWidth}px`;
      });
    }

    const focusTimer = window.setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 80);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.paddingRight = prevBodyPadding;
      fixedEls.forEach((el, i) => {
        el.style.paddingRight = prevFixedPadding[i] ?? "";
      });
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      const reset = window.setTimeout(() => setStatus("idle"), 320);
      return () => window.clearTimeout(reset);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload: Record<string, string> = { "form-name": FORM_NAME };
    formData.forEach((value, key) => {
      payload[key] = typeof value === "string" ? value : "";
    });

    setStatus("submitting");
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setStatus("success");
      form.reset();
    } catch (err) {
      console.error("Demo form submission failed", err);
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="demo-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-modal-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.div
            className="demo-modal"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.985 }}
            transition={{ duration: 0.22, ease: [0.22, 0.6, 0.32, 1] }}
          >
            <button
              type="button"
              className="demo-modal-close"
              onClick={onClose}
              aria-label={t({ sv: "Stäng", en: "Close" })}
            >
              <LuX size={18} aria-hidden />
            </button>

            {status === "success" ? (
              <div className="demo-modal-success">
                <div className="demo-modal-success-icon">
                  <LuCheck size={28} aria-hidden />
                </div>
                <h2 id="demo-modal-title" className="demo-modal-title">
                  {t(demoFormContent.successTitle)}
                </h2>
                <p className="demo-modal-subtitle">
                  {t(demoFormContent.successBody)}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onClose}
                >
                  {t(demoFormContent.successClose)}
                </button>
              </div>
            ) : (
              <>
                <header className="demo-modal-header">
                  <h2 id="demo-modal-title" className="demo-modal-title">
                    {t(demoFormContent.title)}
                  </h2>
                  <p className="demo-modal-subtitle">
                    {t(demoFormContent.subtitle)}
                  </p>
                </header>

                <form
                  name={FORM_NAME}
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  className="demo-modal-form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  {/* Required by Netlify so the runtime form is associated */}
                  <input type="hidden" name="form-name" value={FORM_NAME} />
                  <p className="demo-modal-honey" aria-hidden="true">
                    <label>
                      {t({ sv: "Lämna tom", en: "Leave empty" })}
                      <input
                        type="text"
                        name="bot-field"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </label>
                  </p>

                  <div className="demo-modal-grid">
                    <label className="demo-field">
                      <span>{t(fields.name.label)}</span>
                      <input
                        ref={firstFieldRef}
                        type="text"
                        name="name"
                        required
                        autoComplete="name"
                        placeholder={t(fields.name.placeholder)}
                      />
                    </label>
                    <label className="demo-field">
                      <span>{t(fields.email.label)}</span>
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder={t(fields.email.placeholder)}
                      />
                    </label>
                    <label className="demo-field">
                      <span>{t(fields.organization.label)}</span>
                      <input
                        type="text"
                        name="organization"
                        required
                        autoComplete="organization"
                        placeholder={t(fields.organization.placeholder)}
                      />
                    </label>
                    <label className="demo-field">
                      <span>{t(fields.phone.label)}</span>
                      <input
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        placeholder={t(fields.phone.placeholder)}
                      />
                    </label>
                    <label className="demo-field demo-field-full">
                      <span>{t(fields.message.label)}</span>
                      <textarea
                        name="message"
                        rows={4}
                        placeholder={t(fields.message.placeholder)}
                      />
                    </label>
                  </div>

                  {status === "error" && (
                    <div className="demo-modal-error" role="alert">
                      <strong>{t(demoFormContent.errorTitle)}</strong>
                      <span>{t(demoFormContent.errorBody)}</span>
                    </div>
                  )}

                  <div className="demo-modal-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={onClose}
                      disabled={status === "submitting"}
                    >
                      {t(demoFormContent.cancel)}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting"
                        ? t(demoFormContent.sending)
                        : t(demoFormContent.submit)}
                      <LuArrowRight size={16} aria-hidden />
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
